import React, { useEffect, useState } from 'react';

import { trackEvent } from 'utils/metricsTracking';
import { EVENTS } from 'consts/metricsTrackingEvents';
import { LOCAL_COLORS } from 'styles/constants';

import {
  MainBox,
  InnerBox,
  TextBox,
  MobileIngredientsBox,
  DesktopIngredientsBox,
  DesktopBar,
  disclaimerStyles,
  bodyStyles,
} from './Ingredients.style';
import { IngredientIcon } from './components/IngredientIcon';
import { IngredientDetails } from './components/IngredientDetails';

export interface IngredientsInfoContent {
  name: string;
  title: string;
  detailsText: string;
  iconImage: JSX.Element;
  disclaimer?: string;
}

export interface IngredientsProps {
  /** 50 characters max, preferred ~30 */
  header: JSX.Element | string;
  body?: React.ReactNode;
  /** Choose to use Acne / Rosacea, Anti-Aging, or Dark Spots ingredients */
  ingredients: IngredientsInfoContent[];
  /** Controls the module's background color. */
  backgroundColor?: string;
  /** Controls Ingredient name color + highlight color */
  ingredientTitleColor?: string;
  disclaimerText?: React.ReactNode;
}

export const Ingredients = ({
  header,
  body,
  ingredients,
  backgroundColor = LOCAL_COLORS.surfaceLight,
  ingredientTitleColor,
  disclaimerText = 'Ingredients subject to consultation.',
}: IngredientsProps) => {
  const [activeIngredient, setActiveIngredient] = useState(ingredients[0]);

  useEffect(() => {
    setActiveIngredient(ingredients[0]);
  }, [ingredients]);

  const onIngredientClick = (ingredient: IngredientsInfoContent) => {
    setActiveIngredient(ingredient);
    trackEvent(EVENTS.moduleInteraction, {
      path: window.location.pathname,
      'Module Name': `Ingredients`,
      'Module Element Section': 'Ingredient',
      'Module Element Interaction Type': 'Click',
      'Element Content': ingredient.name,
    });
  };

  const moduleName = 'ingredients';

  return (
    <MainBox data-module={moduleName} backgroundColor={backgroundColor}>
      <InnerBox>
        <div>
          <TextBox>
            <h2>{header}</h2>
            <div css={bodyStyles}>{body}</div>
          </TextBox>
          <DesktopBar />
          <MobileIngredientsBox>
            {ingredients.map((ingredient) => (
              <IngredientIcon
                key={ingredient.name}
                title={ingredient.title}
                isActive={ingredient === activeIngredient}
                clickHandler={() => onIngredientClick(ingredient)}
                backgroundColor={backgroundColor}
                iconImage={ingredient.iconImage}
                titleColor={ingredientTitleColor}
              />
            ))}
          </MobileIngredientsBox>
          <IngredientDetails activeIngredient={activeIngredient} />
          <small css={disclaimerStyles}>
            {activeIngredient?.disclaimer
              ? activeIngredient.disclaimer
              : disclaimerText}
          </small>
        </div>
        <DesktopIngredientsBox>
          {ingredients.map((ingredient) => (
            <IngredientIcon
              key={ingredient.name}
              title={ingredient.title}
              isActive={ingredient === activeIngredient}
              clickHandler={() => onIngredientClick(ingredient)}
              backgroundColor={backgroundColor}
              iconImage={ingredient.iconImage}
              titleColor={ingredientTitleColor}
            />
          ))}
        </DesktopIngredientsBox>
      </InnerBox>
    </MainBox>
  );
};
